import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, Home, Clock, Shield, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const InstallationPage = () => {
  const processSteps = [
    { step: 1, title: "Free Consultation", desc: "We visit your Saskatoon home to assess your needs and take precise measurements" },
    { step: 2, title: "Door Selection", desc: "Choose from 20+ styles and colors that match your Saskatoon home's aesthetic" },
    { step: 3, title: "Professional Installation", desc: "Expert technicians install your new door with precision and care" },
    { step: 4, title: "Testing & Cleanup", desc: "We test all safety features and leave your garage spotless" }
  ];

  const benefits = [
    "Increase home value and curb appeal",
    "Improve energy efficiency with insulated doors",
    "Enhanced security for your Saskatoon property",
    "Quieter operation with modern technology",
    "Warranty protection on parts and labor",
    "Professional installation ensures safety"
  ];

  const doorOptions = [
    { name: "Long Panel", desc: "Modern horizontal design with glass inserts" },
    { name: "Short Panel", desc: "Traditional raised panel for classic look" },
    { name: "Flush", desc: "Contemporary smooth surface" },
    { name: "Carriage", desc: "Rustic charm with decorative hardware" }
  ];

  const faqItems = [
    {
      q: "How long does garage door installation take in Saskatoon?",
      a: "Most installations are completed in 4-6 hours. For Saskatoon homeowners replacing an existing door, we can often complete the work in a single day including removal of the old door."
    },
    {
      q: "What's included in the installation cost?",
      a: "Our Saskatoon installation service includes removal of old door, professional installation of new door, opener setup if needed, safety testing, and cleanup. We provide detailed quotes upfront."
    },
    {
      q: "Do you offer warranties on new doors?",
      a: "Yes! All new garage doors come with manufacturer warranties on parts. We also provide workmanship guarantees for Saskatoon homeowners for complete peace of mind."
    },
    {
      q: "Can you match my home's style?",
      a: "Absolutely! We help Saskatoon homeowners choose doors that complement their home's architecture and color scheme. With 20+ styles available, we'll find the perfect match."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Garage Door Installation Saskatoon
            </h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Professional installation of premium garage doors for Saskatoon homeowners. Increase your home's curb appeal, security, and value with expert installation from 306 Garage Doors.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-6 text-lg">
                <Phone size={22} className="mr-2" />Call {businessInfo.phone}
              </Button>
              <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-lg">
                <MessageSquare size={22} className="mr-2" />Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose New Door */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Why Install a New Garage Door in Saskatoon?</h2>
            <div className="prose prose-lg max-w-none text-gray-700 mb-8">
              <p className="mb-4">
                A new garage door is one of the best investments for Saskatoon homeowners. According to Remodeling Magazine's Cost vs. Value Report, garage door replacement offers excellent return on investment, often recouping 85-95% of the cost when you sell your home.
              </p>
              <p className="mb-4">
                Beyond financial return, a new garage door dramatically improves your Saskatoon home's curb appeal. As one of the largest visible features on many homes, your garage door makes a powerful first impression. Modern designs, colors, and styles can transform your property's appearance.
              </p>
              <p>
                Today's garage doors also offer superior insulation compared to older models. For Saskatoon's cold winters, an insulated garage door (R-16 or R-18) helps maintain comfortable temperatures in your garage and can reduce heating costs for your entire home if the garage is attached.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Our Installation Process for Saskatoon Homes</h2>
            <div className="space-y-6">
              {processSteps.map((item) => (
                <Card key={item.step} className="border-l-4 border-blue-600">
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                          {item.step}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-700 text-lg">{item.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Door Styles */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Door Styles for Saskatoon Homes</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {doorOptions.map((door, idx) => (
                <Card key={idx} className="border-2 hover:border-blue-600 transition-all">
                  <CardHeader>
                    <CardTitle className="text-2xl text-blue-700">{door.name}</CardTitle>
                    <p className="text-gray-600 mt-2">{door.desc}</p>
                  </CardHeader>
                  <CardContent>
                    <Button onClick={() => window.location.href = '/products'} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold">
                      View Options
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Benefits of Professional Installation</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle size={24} className="text-green-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold text-lg">{benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <Home size={48} className="text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-1">Local Business</h3>
                <p className="text-sm text-gray-600">Family-owned in Saskatoon</p>
              </div>
              <div>
                <Shield size={48} className="text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-1">Fully Insured</h3>
                <p className="text-sm text-gray-600">Licensed & insured</p>
              </div>
              <div>
                <Clock size={48} className="text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-1">24/7 Service</h3>
                <p className="text-sm text-gray-600">Always available</p>
              </div>
              <div>
                <DollarSign size={48} className="text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-1">Fair Pricing</h3>
                <p className="text-sm text-gray-600">Transparent quotes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-xl text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready for a New Garage Door in Saskatoon?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Contact 306 Garage Doors today for a free consultation and quote. We serve Saskatoon homeowners with professional installation and quality products.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-12 py-8 text-xl">
              <Phone size={24} className="mr-2" />Call {businessInfo.phone}
            </Button>
            <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-12 py-8 text-xl">
              Request Free Quote
            </Button>
          </div>
          <p className="mt-6 text-blue-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default InstallationPage;